/**
 * RepoManager
 *
 * Manages repository state and operations using per-entity product state:
 * - Repository CRUD
 * - In-memory git info cache (fetched on-demand, not persisted)
 */

import { computed, makeAutoObservable } from "mobx"
import { type BranchInfo, type GitSummaryResponse, gitApi } from "../../nestAPIs/git"
import type { Workspace } from "../../nest/generated"
import { isNestApiAvailable, requestNestMethod } from "../../nest/client"
import type { RepoRecord } from "../../persistence/records"
import { parseProjectChars, normalizeProjectEmoji, normalizeProjectGlyph } from "../../projectAppearance"
import type { Repo } from "../../types"
import type { CodeStore } from "../store"
import { ProjectIdentityGeneration } from "../../projectIdentityGeneration"
import { requestAppOperation } from "../../wildlark/appClient"
import type { AppOperationMap } from "../../wildlark/app-generated"
import { parseRepoRecord } from "../../persistence/records"
import { ulid } from "../../utils/ulid"

/** Git info - computed at runtime, not persisted */
export interface GitInfo {
    isGitRepo: boolean
    repoRoot: string
    relativePath: string
    mainBranch: string
    hasGhCli: boolean
}

function requireNestWorkspaceApi(): void {
    if (!isNestApiAvailable()) throw new Error("Nest workspace API is required")
}

function repoFromRecord(record: RepoRecord, store: CodeStore): Repo {
    return {
        id: record.id,
        name: record.name,
        path: record.path,
        createdBy: store.currentUser,
        createdAt: record.createdAt,
        updatedAt: record.updatedAt,
        archived: record.archived,
        color: record.color,
        glyph: record.glyph,
        projectChars: record.projectChars,
        projectEmoji: record.projectEmoji,
    }
}

function repoRecordFromWorkspace(workspace: Workspace, existing?: RepoRecord): RepoRecord {
    return {
        ...existing,
        v: 1,
        id: workspace.workspaceId,
        name: workspace.name,
        path: workspace.primaryDir,
        workspaceId: workspace.workspaceId,
        createdAt: workspace.createdAt,
        updatedAt: workspace.updatedAt,
    }
}

export class RepoManager {
    reposLoading = false
    // In-memory cache (cleared on app restart, or when repo.path changes)
    private gitInfoCache: Map<string, GitInfo | null> = new Map()
    private identityGeneration: ProjectIdentityGeneration | null = null

    constructor(private store: CodeStore) {
        makeAutoObservable<RepoManager, "store" | "identityGeneration">(this, {
            repos: computed,
            store: false,
            identityGeneration: false,
        })
    }

    startIdentityGeneration(): void {
        this.stopIdentityGeneration()
        if (this.store.isGuest) return
        this.identityGeneration = new ProjectIdentityGeneration(this.store.requireProductState(), () => this.store.defaultHarnessId)
        this.identityGeneration.start()
    }

    stopIdentityGeneration(): void {
        this.identityGeneration?.stop()
        this.identityGeneration = null
    }

    // ==================== Computed from RepoStore ====================

    get repos(): Repo[] {
        const productState = this.store.productState
        return productState ? [...productState.repos.values()].map((record) => repoFromRecord(record, this.store)) : []
    }

    // ==================== CRUD Operations ====================

    getRepo(id: string): Repo | undefined {
        const record = this.store.productState?.repos.get(id)
        if (record) return repoFromRecord(record, this.store)
        return this.repos.find((r) => r.id === id)
    }

    async addRepo(params: { name: string; path: string }): Promise<Repo | null> {
        return this.createRepo(params)
    }

    async createRepo(params: { name: string; path: string }): Promise<Repo | null> {
        return this.createProject({ kind: "existing", ...params, clientRequestId: ulid() })
    }

    async createProject(params: AppOperationMap["project.create"]["params"]): Promise<Repo | null> {
        const productState = this.store.requireProductState()
        requireNestWorkspaceApi()
        const result = await requestAppOperation("project.create", params)
        if (this.store.productState !== productState) throw new Error("The active computer changed. Reopen discovery on the intended computer.")
        const record = parseRepoRecord(result.project)
        if (!record) throw new Error("The computer returned an invalid project record")
        await productState.refreshRepo(record.id, record.workspaceId)
        if (result.warnings?.length) console.warn("[RepoManager] Project created with warnings", result.warnings)
        return this.getRepo(record.id) ?? null
    }

    async updateRepo(id: string, updates: Partial<Pick<Repo, "name" | "path" | "color" | "glyph" | "projectChars" | "projectEmoji">>): Promise<Repo | null> {
        const productState = this.store.requireProductState()

        // Clear git info cache if path changed
        if (updates.path !== undefined) {
            this.gitInfoCache.delete(id)
        }

        const existing = productState.repos.get(id)
        if (!existing) return null

        let next = existing
        if (updates.name !== undefined || updates.path !== undefined) {
            requireNestWorkspaceApi()
            const result = await requestNestMethod("workspace.update", {
                workspaceId: id,
                name: updates.name ?? existing.name,
                primaryDir: updates.path ?? existing.path,
            })
            next = repoRecordFromWorkspace(result.workspace, existing)
        } else {
            next = { ...existing, updatedAt: new Date().toISOString() }
        }

        if ("color" in updates) {
            if (updates.color) next.color = updates.color
            else delete next.color
        }
        if ("glyph" in updates) {
            if (updates.glyph) next.glyph = normalizeProjectGlyph(updates.glyph)
            else delete next.glyph
        }
        if ("projectChars" in updates) {
            const chars = parseProjectChars(updates.projectChars)
            if (chars) next.projectChars = chars
            else delete next.projectChars
        }
        if ("projectEmoji" in updates) {
            const emoji = normalizeProjectEmoji(updates.projectEmoji ?? "")
            if (emoji) next.projectEmoji = emoji
            else delete next.projectEmoji
        }

        await productState.putRepo(next)
        return this.getRepo(id) ?? null
    }

    async setRepoArchived(id: string, archived: boolean): Promise<void> {
        const productState = this.store.requireProductState()
        requireNestWorkspaceApi()
        const existing = productState.repos.get(id)
        if (!existing) return
        await productState.putRepo({ ...existing, archived, updatedAt: new Date().toISOString() })
    }

    async removeRepo(id: string): Promise<boolean> {
        return this.deleteRepo(id)
    }

    async deleteRepo(id: string): Promise<boolean> {
        const productState = this.store.requireProductState()

        // Clean up cache
        this.gitInfoCache.delete(id)

        requireNestWorkspaceApi()
        const result = await requestNestMethod("workspace.delete", { workspaceId: id })
        if (result.deleted) {
            await productState.deleteRepo(id)
        }
        return result.deleted
    }

    // ==================== Git Info (In-Memory Cache) ====================

    /**
     * Get git info for a repo, fetching from Electron if not cached.
     * Returns null if the directory is not a git repo.
     */
    async getGitInfo(repoId: string): Promise<GitInfo | null> {
        const repo = this.getRepo(repoId)
        if (!repo) return null

        // Check cache
        if (this.gitInfoCache.has(repoId)) {
            return this.gitInfoCache.get(repoId) ?? null
        }

        // Host-path Git inspection is node-scoped; a guest gets the plain project.
        if (this.store.isGuest) {
            this.gitInfoCache.set(repoId, null)
            return null
        }

        // Fetch from Electron
        try {
            const response = await gitApi.isGitDirectory({ directory: repo.path })
            const gitInfo = response.isGitDirectory
                ? {
                      isGitRepo: true,
                      repoRoot: response.repoRoot,
                      relativePath: response.relativePath,
                      mainBranch: response.mainBranch,
                      hasGhCli: response.hasGhCli,
                  }
                : null

            this.gitInfoCache.set(repoId, gitInfo)
            return gitInfo
        } catch (err) {
            console.error(`[RepoManager] Failed to detect git info for ${repoId}:`, err)
            this.gitInfoCache.set(repoId, null)
            return null
        }
    }

    /** Check if git info is cached (without fetching) */
    getGitInfoSync(repoId: string): GitInfo | null | undefined {
        return this.gitInfoCache.get(repoId)
    }

    /** Clear cached git info for a repo */
    clearGitInfoCache(repoId: string): void {
        this.gitInfoCache.delete(repoId)
    }

    /**
     * Re-check gh CLI availability and update the cached git info.
     * Returns the fresh hasGhCli value.
     */
    async refreshGhCliStatus(repoId: string): Promise<boolean> {
        try {
            const result = await gitApi.checkGhCli()
            const hasGhCli = result.hasGhCli

            // Update the cached git info entry if it exists
            const cached = this.gitInfoCache.get(repoId)
            if (cached) {
                cached.hasGhCli = hasGhCli
            }

            return hasGhCli
        } catch (err) {
            console.error(`[RepoManager] Failed to refresh gh CLI status for ${repoId}:`, err)
            return false
        }
    }

    /** Check if a path is a git repo - returns true/false */
    async isGitRepo(directory: string): Promise<boolean> {
        try {
            const response = await gitApi.isGitDirectory({ directory })
            return response.isGitDirectory
        } catch {
            return false
        }
    }

    // ==================== Git Operations ====================

    /**
     * List branches for a repo.
     * Requires git info to be fetched first.
     */
    async listBranches(
        repoId: string,
        { includeRemote = false }: { includeRemote?: boolean } = {}
    ): Promise<{
        branches: BranchInfo[]
        defaultBranch: string
    }> {
        const gitInfo = await this.getGitInfo(repoId)
        if (!gitInfo) {
            return { branches: [], defaultBranch: "main" }
        }

        if (!gitApi.isAvailable()) {
            return { branches: [], defaultBranch: gitInfo.mainBranch }
        }

        return gitApi.listBranches({
            workspaceId: repoId,
            workspaceRoot: gitInfo.repoRoot,
            includeRemote,
        })
    }

    /**
     * Get git status for a repo.
     * Requires git info to be fetched first.
     */
    async getGitSummary(repoId: string): Promise<GitSummaryResponse> {
        const gitInfo = await this.getGitInfo(repoId)
        if (!gitInfo) {
            return {
                branch: null,
                headCommit: "",
                ahead: null,
                hasChanges: false,
                staged: { files: [], stats: { filesChanged: 0, binaryFiles: 0, insertions: 0, deletions: 0 } },
                unstaged: { files: [], stats: { filesChanged: 0, binaryFiles: 0, insertions: 0, deletions: 0 } },
                untracked: { files: [], stats: { filesChanged: 0, binaryFiles: 0, insertions: 0, deletions: 0 } },
                total: { filesChanged: 0, binaryFiles: 0, insertions: 0, deletions: 0 },
            }
        }

        if (!gitApi.isAvailable()) {
            return {
                branch: null,
                headCommit: "",
                ahead: null,
                hasChanges: false,
                staged: { files: [], stats: { filesChanged: 0, binaryFiles: 0, insertions: 0, deletions: 0 } },
                unstaged: { files: [], stats: { filesChanged: 0, binaryFiles: 0, insertions: 0, deletions: 0 } },
                untracked: { files: [], stats: { filesChanged: 0, binaryFiles: 0, insertions: 0, deletions: 0 } },
                total: { filesChanged: 0, binaryFiles: 0, insertions: 0, deletions: 0 },
            }
        }

        return gitApi.getGitSummary({
            workspaceId: repoId,
            workspaceRoot: gitInfo.repoRoot,
        })
    }

    // ==================== Loading ====================

    async loadRepos(): Promise<void> {
        if (this.reposLoading) return

        this.reposLoading = true
        try {
            // Initialize stores if not done
            await this.store.initializeStores()
        } finally {
            this.reposLoading = false
        }
    }
}
